'use client';

import { Link } from 'react-router-dom';
import { V2Layout, RevealSection, useV2Lang, Eyebrow, Rule, ACCENT, HAIRLINE, PageHeader, DisplayTitle } from '../_layout';

const COPY = {
  CN: {
    kicker: '诗歌 · Qiaodong 桥洞',
    title: '让两种互相\n争辩的语言\n对话。',
    lede: '桥洞是 PathIN 的诗歌板块。我们做翻译工作坊、节展策划、双语出版——让中德诗歌不仅被翻译，更彼此追问。',
    items: {
      title: '我们做什么',
      list: [
        { kicker: '01', title: '翻译工作坊', body: '中国诗人与德语译者两两配对，互译、互问、互修。每一次产出一个完整的双语对作。与 Haus für Poesie 长期合作。' },
        { kicker: '02', title: '节展策划', body: '受 Haus für Poesie 邀请，2025 年策划了第 26 届柏林诗歌节中国专场——把九零后中国诗人带到 Akademie der Künste。' },
        { kicker: '03', title: '双语出版', body: '正在编纂的双语选集《生活与洞见》——不只是译诗，而是把中德诗歌之间的对话变成一本可以握在手里的书。' },
        { kicker: '04', title: '研讨与朗读', body: '在 Silent Green 等场所组织翻译研讨与公开朗读，让诗人和译者彼此见面。' },
      ],
    },
    festival: {
      kicker: '2025 节展精选',
      title: '第 26 届柏林诗歌节中国专场',
      subtitle: '"朦胧的诗与具体的人"',
      body: '受 Haus für Poesie 邀请，桥洞策划了 2025 年柏林诗歌节主舞台核心时段的中国专场。我们带着九零后中国诗人来到 Akademie der Künste，做了一场只有彼时彼地能发生的双语朗读——诗的朦胧与人的具体、彼此映照。',
      meta: '2025 年 6 月 14 日 · Akademie der Künste · Hanseatenweg, Berlin',
      cta: '阅读详情 →',
      href: '/v2/events/2',
    },
    workshop: {
      kicker: '长期工作坊',
      title: '中德诗歌翻译工作坊',
      body: '与 Haus für Poesie 联合的长期项目。中国诗人与德语译者结对，每一对完成一组完整翻译——但更重要的是让两种诗学方法相互拷问。我们相信好的翻译不只是"把意思搬过去"，而是让两种诗在同一个房间里彼此看到。',
      meta: '2025 年 4 月起 · Haus für Poesie · 双语选集《生活与洞见》编纂中',
    },
    anthology: {
      kicker: '正在出版',
      title: '《生活与洞见》',
      subtitle: 'Leben und Einsicht — A Bilingual Anthology',
      body: '一本中德双语的当代诗歌选集，按主题编排——劳作、家庭、城市、记忆、远方。每首诗都有原文、译文、译者的笔记，以及一段两位作者关于这首诗的简短对话。Haus für Poesie 联合出品，正在排版中。',
    },
    contact: { kicker: '想参与？', body: '诗人、译者、出版人、读者——欢迎联系。我们正在收集 2026 年工作坊的报名意向。', cta: '写信给 桥洞 →' },
  },
  EN: {
    kicker: 'Poetry · Qiaodong 桥洞',
    title: 'Letting two\nlanguages that\nargue, talk.',
    lede: "Qiaodong is the poetry side of PathIN. Translation workshops, festival programming, bilingual publishing — making sure Chinese and German poetry don't just get translated, but actually argue with each other.",
    items: {
      title: 'What we do',
      list: [
        { kicker: '01', title: 'Translation workshops', body: 'Chinese poets paired with German translators in twos. They translate each other, question each other, revise each other. Each pair produces a complete bilingual work. Long-term partnership with Haus für Poesie.' },
        { kicker: '02', title: 'Festival programming', body: "Invited by Haus für Poesie, in 2025 we programmed the China main-stage at the 26th Berlin Poetry Festival — bringing 90s-born Chinese poets to Akademie der Künste." },
        { kicker: '03', title: 'Bilingual publishing', body: "The bilingual anthology 《生活与洞见》 (Life and Insight), in preparation — not just translated poems, but the conversation between Chinese and German poetry made into a book you can hold." },
        { kicker: '04', title: 'Symposia & readings', body: "Translation symposia and public readings at Silent Green and other venues — letting poets and translators meet each other in person." },
      ],
    },
    festival: {
      kicker: '2025 festival showcase',
      title: '26th Berlin Poetry Festival · China Program',
      subtitle: '"Misty Poems and Specific People"',
      body: "Invited by Haus für Poesie, Qiaodong programmed the main-stage primetime slot at the 2025 Berlin Poetry Festival. We brought 90s-born Chinese poets to Akademie der Künste for a bilingual reading that could only happen there, then — the mist of poetry and the specificity of people, mirroring each other.",
      meta: 'June 14, 2025 · Akademie der Künste · Hanseatenweg, Berlin',
      cta: 'Read full case →',
      href: '/v2/events/2',
    },
    workshop: {
      kicker: 'Long-term workshop',
      title: 'Chinese-German Poetry Translation Workshop',
      body: "A long-term project in partnership with Haus für Poesie. Chinese poets pair with German translators; each pair completes a translation set — but the deeper aim is letting two poetic methods cross-examine each other. We believe good translation isn't just 'moving meaning across' but making two poems see each other in the same room.",
      meta: 'Since April 2025 · Haus für Poesie · Bilingual anthology 《生活与洞见》 in preparation',
    },
    anthology: {
      kicker: 'In production',
      title: '《生活与洞见》',
      subtitle: 'Leben und Einsicht — A Bilingual Anthology',
      body: 'A Chinese-German bilingual anthology of contemporary poetry, organized thematically: labor, family, the city, memory, distance. Each poem includes the original, a translation, the translator\'s note, and a short dialogue between the two authors about it. Co-published with Haus für Poesie. In typesetting.',
    },
    contact: { kicker: 'Get involved?', body: 'Poets, translators, publishers, readers — write to us. We are collecting expressions of interest for 2026 workshops.', cta: 'Email Qiaodong →' },
  },
  DE: {
    kicker: 'Poesie · Qiaodong 桥洞',
    title: 'Zwei Sprachen,\ndie miteinander\nstreiten — und reden.',
    lede: 'Qiaodong ist der Poesie-Bereich von PathIN. Übersetzungs-Workshops, Festival-Programmierung, zweisprachiges Publishing — damit chinesische und deutsche Lyrik nicht nur übersetzt, sondern wirklich miteinander ins Streitgespräch tritt.',
    items: {
      title: 'Was wir machen',
      list: [
        { kicker: '01', title: 'Übersetzungs-Workshops', body: 'Chinesische Lyriker:innen werden mit deutschsprachigen Übersetzer:innen zu Paaren. Sie übersetzen einander, hinterfragen einander, überarbeiten einander. Jedes Paar produziert ein vollständig zweisprachiges Werk. Langfristige Partnerschaft mit dem Haus für Poesie.' },
        { kicker: '02', title: 'Festival-Programmierung', body: 'Auf Einladung des Hauses für Poesie kuratierten wir 2025 das China-Programm der Hauptbühne beim 26. Poesiefestival Berlin — mit chinesischen Lyriker:innen der 90er-Generation in der Akademie der Künste.' },
        { kicker: '03', title: 'Zweisprachiges Publishing', body: 'Die zweisprachige Anthologie 《生活与洞见》 (Leben und Einsicht), in Arbeit — nicht nur übersetzte Gedichte, sondern das Gespräch zwischen chinesischer und deutscher Lyrik als Buch, das man in der Hand halten kann.' },
        { kicker: '04', title: 'Symposien & Lesungen', body: 'Übersetzungs-Symposien und öffentliche Lesungen im Silent Green und anderswo — damit Lyriker:innen und Übersetzer:innen einander persönlich begegnen.' },
      ],
    },
    festival: {
      kicker: '2025 Festival-Programm',
      title: '26. Poesiefestival Berlin · China-Programm',
      subtitle: '"Verschleierte Gedichte und konkrete Menschen"',
      body: 'Auf Einladung des Hauses für Poesie kuratierte Qiaodong den Hauptbühnen-Primetime-Slot des Poesiefestivals Berlin 2025. Wir brachten chinesische Lyriker:innen der 90er-Generation in die Akademie der Künste — für eine zweisprachige Lesung, die nur dort und damals stattfinden konnte. Der Schleier der Poesie und die Konkretheit der Menschen spiegeln sich.',
      meta: '14. Juni 2025 · Akademie der Künste · Hanseatenweg, Berlin',
      cta: 'Vollständige Fallstudie →',
      href: '/v2/events/2',
    },
    workshop: {
      kicker: 'Laufender Workshop',
      title: 'Chinesisch-deutscher Übersetzungs-Workshop',
      body: 'Ein langfristiges Projekt in Partnerschaft mit dem Haus für Poesie. Chinesische Lyriker:innen werden mit deutschsprachigen Übersetzer:innen gepaart; jedes Paar fertigt einen Übersetzungssatz — aber das tiefere Ziel ist, zwei poetische Methoden einander befragen zu lassen. Gute Übersetzung ist nicht nur "Bedeutung übertragen", sondern zwei Gedichte im selben Raum einander sehen lassen.',
      meta: 'Seit April 2025 · Haus für Poesie · Zweisprachige Anthologie 《生活与洞见》 in Vorbereitung',
    },
    anthology: {
      kicker: 'In Produktion',
      title: '《生活与洞见》',
      subtitle: 'Leben und Einsicht — Eine zweisprachige Anthologie',
      body: 'Eine chinesisch-deutsche Anthologie zeitgenössischer Lyrik, thematisch geordnet: Arbeit, Familie, Stadt, Erinnerung, Ferne. Jedes Gedicht enthält das Original, eine Übersetzung, die Notiz der Übersetzer:in und einen kurzen Dialog zwischen den beiden Autor:innen darüber. Co-Publikation mit dem Haus für Poesie. Im Satz.',
    },
    contact: { kicker: 'Mitmachen?', body: 'Lyriker:innen, Übersetzer:innen, Verleger:innen, Leser:innen — schreiben Sie uns. Wir sammeln Interessensbekundungen für die Workshops 2026.', cta: 'Qiaodong schreiben →' },
  },
};

export default function V2Qiaodong() {
  const lang = useV2Lang();
  const t = COPY[lang];

  return (
    <V2Layout hideFooterContact>
      <PageHeader kicker={t.kicker} title={<DisplayTitle text={t.title} />} lede={t.lede} />

      <Rule />

      {/* What we do */}
      <RevealSection className="px-6 md:px-10 py-24">
        <div className="mx-auto max-w-[1400px]">
          <h2 className="v2-cjk-tight text-[clamp(28px,3vw,44px)] leading-[1.15] tracking-[-0.025em] font-bold mb-12">{t.items.title}</h2>
          <div className="grid gap-x-12 md:grid-cols-2 border-t" style={{ borderColor: HAIRLINE }}>
            {t.items.list.map((s) => (
              <div key={s.kicker} className="border-b py-10 md:py-12 md:pr-8" style={{ borderColor: HAIRLINE }}>
                <span className="font-mono text-[11px] tracking-[0.18em] uppercase" style={{ color: ACCENT }}>{s.kicker}</span>
                <h3 className="v2-cjk-tight mt-3 text-[clamp(22px,2vw,30px)] leading-[1.25] tracking-[-0.02em] font-bold">{s.title}</h3>
                <p className="mt-4 text-[15px] leading-[1.7] text-white/80 max-w-[55ch]">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* Festival showcase */}
      <RevealSection className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3"><Eyebrow accent>{t.festival.kicker}</Eyebrow></div>
          <Link to={t.festival.href} className="md:col-span-9 group block">
            <h3 className="v2-cjk-tight text-[clamp(32px,4.4vw,68px)] leading-[1.05] tracking-[-0.035em] font-black max-w-[20ch]">{t.festival.title}</h3>
            <p className="mt-3 text-[clamp(18px,1.8vw,26px)] italic text-white/75">{t.festival.subtitle}</p>
            <p className="mt-8 text-[clamp(16px,1.3vw,19px)] leading-[1.7] text-white/75 max-w-[60ch]">{t.festival.body}</p>
            <p className="mt-8 font-mono text-[12px] tracking-[0.05em] text-white/65 font-medium">{t.festival.meta}</p>
            <span className="inline-block mt-8 text-[14px] border-b-4 pb-2 font-semibold tracking-wide" style={{ borderColor: ACCENT, color: ACCENT }}>{t.festival.cta}</span>
          </Link>
        </div>
      </RevealSection>

      <Rule />

      {/* Workshop */}
      <RevealSection className="px-6 md:px-10 py-24">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3"><Eyebrow>{t.workshop.kicker}</Eyebrow></div>
          <div className="md:col-span-9">
            <h3 className="v2-cjk-tight text-[clamp(28px,3.6vw,48px)] leading-[1.15] tracking-[-0.035em] font-extrabold max-w-[20ch]">{t.workshop.title}</h3>
            <p className="mt-6 text-[16px] leading-[1.7] text-white/80 max-w-[60ch]">{t.workshop.body}</p>
            <p className="mt-6 font-mono text-[12px] tracking-[0.05em] text-white/65 font-medium">{t.workshop.meta}</p>
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* Anthology */}
      <RevealSection className="px-6 md:px-10 py-24">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3"><Eyebrow>{t.anthology.kicker}</Eyebrow></div>
          <div className="md:col-span-9">
            <h3 className="v2-cjk-tight text-[clamp(36px,5vw,72px)] leading-[1.05] tracking-[-0.04em] font-black">{t.anthology.title}</h3>
            <p className="mt-3 text-[clamp(18px,1.8vw,28px)] italic text-white/75">{t.anthology.subtitle}</p>
            <p className="mt-8 text-[16px] leading-[1.7] text-white/80 max-w-[60ch]">{t.anthology.body}</p>
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* Contact */}
      <RevealSection className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3"><Eyebrow accent>{t.contact.kicker}</Eyebrow></div>
          <div className="md:col-span-9">
            <p className="text-[clamp(20px,2vw,28px)] leading-[1.4] text-white/85 max-w-[40ch]">{t.contact.body}</p>
            <a href="mailto:qiaodong@pathin.eu" className="inline-block mt-8 text-[15px] border-b-4 pb-2 font-semibold tracking-wide hover:no-underline" style={{ borderColor: ACCENT, color: ACCENT }}>{t.contact.cta}</a>
          </div>
        </div>
      </RevealSection>

      <Rule />
    </V2Layout>
  );
}
